import Heading from '@pelckmans/elementis/components/heading';
import Link from '@pelckmans/elementis/components/link';
import SelectField from '@pelckmans/elementis/components/select-field';
import TextField from '@pelckmans/elementis/components/text-field';
import TrashIcon from '@pelckmans/elementis/icons/trash';
import { useContext, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Group } from '../../../../../api/groups';
import ModalContext from '../../../../../contexts/ModalContext';
import { useDefaultToast } from '../../../../../hooks/useDefaultToast';
import useThrowAsyncError from '../../../../../hooks/useThrowAsyncError';
import { sortByName } from '../../../../../utils/sortByName';
import { deleteGroup, fetchSubjects, Subject, updateGroup } from '../../../api/MyGroupsApi';
import { SchoolYear } from '../../../context/MyGroupsContext';
import RemoveGroupConfirmationModal from '../../remove-group-confirmation/RemoveGroupConfirmationModal';
import GroupDetailSubjectsSelector from './components/GroupDetailSubjectsSelector';

const BASE = 'PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.INFO.BODY';
const REMOVE_GROUP_CONFIRMATION_MODAL_ID = 'remove-group-confirmation-modal';

type FormValues = {
  groupName: string;
  subjects: Subject[];
};

type Props = {
  group: Group;
  editable: boolean;
  schoolYear: SchoolYear;
  onGroupUpdate: (groupWithNewName: Group) => void;
  formId: string;
  onGroupDelete: (deletedGroup: Group) => void;
  onClose: () => void;
};

function InfoContent({ group, editable, schoolYear, onGroupUpdate, formId, onGroupDelete, onClose }: Props) {
  const { t, i18n } = useTranslation();
  const handleError = useThrowAsyncError();
  const [isLoading, setIsLoading] = useState(false);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const isSmartschoolGroup = group.school.isSmartschool;
  const { successToast, failedToast } = useDefaultToast();

  const { closeModal, showModal } = useContext(ModalContext);

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
    getValues,
    setValue,
  } = useFormContext<FormValues>();

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        const fetched = await fetchSubjects();
        setSubjects(fetched.sort(sortByName(i18n.language)));
      } catch (err) {
        handleError(err);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [i18n.language, handleError]);

  useEffect(() => {
    if (!subjects.length || isDirty) return;

    const mappedSubjects = group.subjects
      ? group.subjects.map(
          subject => ({ id: subjects.find(s => s.name === subject)?.id || '', name: subject } as Subject),
        )
      : [];

    setValue('subjects', mappedSubjects, { shouldDirty: false });
  }, [subjects, setValue, isDirty, group.subjects]);

  const onSubmit = async () => {
    const values = getValues();
    const { groupName, subjects: selectedSubjects } = values;

    const nameChanged = !isSmartschoolGroup && groupName !== group.name ? groupName : undefined;

    const subjectsChanged =
      selectedSubjects.length !== group.subjects?.length ||
      !selectedSubjects.every(s => group.subjects?.includes(s.name))
        ? selectedSubjects
        : undefined;

    if (!nameChanged && !subjectsChanged) {
      successToast();
      return;
    }
    try {
      // Only send changes to the backend
      const updatedGroup = await updateGroup(schoolYear, group.id, {
        ...(nameChanged && { name: nameChanged }),
        ...(subjectsChanged && { subjects: subjectsChanged.map(s => s.id) }),
      });

      onGroupUpdate({ ...group, subjects: updatedGroup.subjects, name: updatedGroup.name });
      reset({
        groupName: updatedGroup.name,
        subjects: selectedSubjects,
      });
      successToast();
    } catch (err) {
      failedToast();
    }
  };

  const openConfirmationModal = () => {
    showModal(
      <RemoveGroupConfirmationModal
        id={REMOVE_GROUP_CONFIRMATION_MODAL_ID}
        group={group}
        schoolYear={schoolYear}
        onConfirm={handleDelete}
      />,
    );
  };

  const handleDelete = async () => {
    try {
      await deleteGroup(schoolYear, group.id);

      onGroupDelete(group);

      successToast();

      closeModal(REMOVE_GROUP_CONFIRMATION_MODAL_ID);

      onClose();
    } catch (err) {
      failedToast();
    }
  };

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)} name={formId} noValidate className="group-details-form">
      <Heading level={4} removeSpacing="all" className="modal-content-text-title">
        {t(`${BASE}.TITLE`)}
      </Heading>
      <div className="form-fields">
        <TextField
          labelText={t(`${BASE}.FORM.GROUP.LABEL`)}
          placeholder={t(`${BASE}.FORM.GROUP.PLACEHOLDER`)}
          disabled={!editable || isSmartschoolGroup}
          maxLength={100}
          error={!!errors.groupName}
          errorText={errors.groupName?.message}
          required
          {...register('groupName', {
            validate: v => (editable && (!v || !v.trim()) ? t(`${BASE}.FORM.GROUP.ERROR`) : true),
          })}
        />

        <SelectField
          id="schools"
          disabled
          required
          onChange={() => {}}
          value={group.school}
          renderValueContent={option => option.name}
          labelText={t(`${BASE}.FORM.SCHOOL.LABEL`)}
          placeholder={t(`${BASE}.FORM.SCHOOL.PLACEHOLDER`)}
          dropdownMenuProps={{
            ariaLabel: t(`${BASE}.FORM.SCHOOL.A11Y_LABEL`),
          }}
        />

        <Controller
          control={control}
          name="subjects"
          rules={{
            validate: v => (editable && v.length === 0 ? t(`${BASE}.FORM.SUBJECT.ERROR`) : true),
          }}
          render={({ field }) => (
            <GroupDetailSubjectsSelector
              editable={editable}
              subjects={subjects}
              isLoadingSubjects={isLoading}
              handleChange={field.onChange}
              handleBlur={field.onBlur}
              formSubjects={field.value}
              errorText={errors.subjects?.message}
            />
          )}
        />
        <Link prominence="danger" icon={<TrashIcon />} onClick={openConfirmationModal} to="#">
          {t(`${BASE}.FORM.ACTIONS.DELETE`)}
        </Link>
      </div>
    </form>
  );
}

export default InfoContent;
